import { useEffect, useRef, forwardRef, createContext, useContext, isValidElement, type HTMLAttributes } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../lib/cn';

interface DialogContextValue {
  onClose?: () => void;
}
const DialogContext = createContext<DialogContextValue>({});

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | 'full';
  className?: string;
  children: React.ReactNode;
}

export function Dialog({ open, onClose, size = 'md', className, children }: DialogProps) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  const sizeClasses: Record<string, string> = {
    sm: 'max-w-sm w-full',
    md: 'max-w-lg w-full',
    lg: 'max-w-2xl w-full',
    xl: 'max-w-4xl w-[96vw] sm:w-[90vw]',
    '2xl': 'max-w-6xl w-[96vw] sm:w-[94vw]',
    '3xl': 'max-w-7xl w-[98vw] sm:w-[96vw]',
    '4xl': 'max-w-[1600px] w-[98vw] sm:w-[96vw]',
    full: 'max-w-[99vw] w-[99vw] max-h-[96vh] h-[96vh]',
  };

  const isChildDialogContent =
    isValidElement(children) &&
    (children.type === DialogContent || (children.type as any)?.displayName === 'DialogContent');

  return createPortal(
    <DialogContext.Provider value={{ onClose }}>
      <DialogOverlay
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
        ref={overlayRef}
      >
        {isChildDialogContent ? (
          children
        ) : (
          <DialogContent size={sizeClasses[size] || sizeClasses.md} className={className}>
            {children}
          </DialogContent>
        )}
      </DialogOverlay>
    </DialogContext.Provider>,
    document.body
  );
}

export const DialogOverlay = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-foreground/40 backdrop-blur-sm overflow-y-auto", className)}
      {...props}
    />
  )
);
DialogOverlay.displayName = "DialogOverlay";

export const DialogContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement> & { size?: string; hideCloseButton?: boolean }>(
  ({ className, size, hideCloseButton = false, children, onClick, ...props }, ref) => {
    const ctx = useContext(DialogContext);

    return (
      <div
        ref={ref}
        className={cn(
          "relative z-50 flex flex-col w-full max-h-[92dvh] sm:max-h-[88vh] bg-card rounded-xl shadow-xl p-4 sm:p-6 animate-in fade-in zoom-in-95 duration-200 overflow-y-auto overscroll-contain my-auto",
          size,
          className
        )}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.(e);
        }}
        {...props}
      >
        {!hideCloseButton && ctx.onClose && (
          <DialogClose onClick={ctx.onClose} />
        )}
        {children}
      </div>
    );
  }
);
DialogContent.displayName = "DialogContent";

export const DialogHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("flex flex-col gap-1.5 text-center sm:text-left shrink-0 pr-8", className)}
      {...props}
    />
  )
);
DialogHeader.displayName = "DialogHeader";

export const DialogFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "sticky -bottom-4 sm:-bottom-6 z-20 bg-card/95 backdrop-blur-xs -mx-4 px-4 sm:-mx-6 sm:px-6 pt-3 pb-3 sm:pb-4 border-t border-border/80 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:space-x-0 mt-auto shrink-0 rounded-b-xl",
        className
      )}
      {...props}
    />
  )
);
DialogFooter.displayName = "DialogFooter";

export function DialogClose({ className, onClick, ...props }: HTMLAttributes<HTMLButtonElement>) {
  const ctx = useContext(DialogContext);
  const handleClick = onClick || ctx.onClose;

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Close dialog"
      title="Close (Esc)"
      className={cn(
        "absolute right-3.5 top-3.5 z-50 p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/80 active:scale-95 transition-all focus:outline-none focus:ring-2 focus:ring-primary/40 cursor-pointer shadow-2xs",
        className
      )}
      {...props}
    >
      <X className="h-4 w-4" />
      <span className="sr-only">Close</span>
    </button>
  );
}
